/*
  Legal-entity transitions: which company each person is employed by today, and which one they end up
  in. Ported from `openEntityModal` / `renderEntityModal` (orgchart.html:8278-8426).

  THE FOUR COMMANDS BEHIND THIS SCREEN HAVE BEEN COMPLETE AND UNREACHABLE since P3 — `addEntity`,
  `updateEntity`, `deleteEntity`, `remapEntity`, with `computeEntityFlows` and a four-way
  classification beside them, every one tested and none with a button. Legal-entity separation is not
  a nice-to-have on a carve-out; it is one of the things the deal legally IS.

  TWO TABLES THAT LOOK SIMILAR AND ARE NOT.

  The MAPPING is per scenario and per perspective: it says "everyone whose imported entity is X reads
  as Y in this scenario's carve-out view". It is a `remapEntity`, stored against the NAME.

  The REGISTRY is project-wide: it says what entities exist and what role each plays. It is
  `addEntity` / `updateEntity` / `deleteEntity`.

  So an as-is entity is read-only in the registry — you rename it through the mapping, because
  renaming the registry row would not move anybody — and deleting a registry row declassifies it
  rather than moving its people. Both of those are the frozen app's rules and both are stated on
  screen rather than left to be discovered.
*/
import { useMemo, useState } from 'react';
import type { Command, EntityClassification, Perspective } from '@integrate/contracts';
import {
  ENTITY_CLASS_META,
  computeEntityFlows,
  entityClassOf,
  fmt,
  fmtMoneyCompact,
  getEntityNames,
  metricFn,
  type MetricKey,
  type SeparationState,
  offeredMetrics,
} from '@integrate/core';
import { Workbench } from '../ui/Workbench.js';
import { EntitySankey } from './EntitySankey.js';
import { useLiveInput } from './live-input.js';
import { MetricToggle } from './MetricToggle.js';
import { ICON, IconButton } from '../ui/Icon.js';

const CLASSES = Object.keys(ENTITY_CLASS_META) as EntityClassification[];

/** The two sides a mapping can describe. `as-is` is the imported data and is never edited here. */
const SIDES: { value: Extract<Perspective, 'remain' | 'carve-out'>; label: string }[] = [
  { value: 'carve-out', label: 'Carve-out' },
  { value: 'remain', label: 'RemainCo' },
];

export function EntityPanel({
  state,
  scenarioId,
  readOnly,
  initialPerspective,
  onDispatch,
  onOpenMatrix,
  onClose,
}: {
  state: SeparationState;
  scenarioId: string;
  readOnly: boolean;
  /** Opens on the side you were already looking at, like the frozen app does. */
  initialPerspective: Perspective;
  onDispatch(commands: Command | Command[]): void;
  onOpenMatrix(): void;
  onClose(): void;
}): JSX.Element {
  const [perspective, setPerspective] = useState<'remain' | 'carve-out'>(
    initialPerspective === 'remain' ? 'remain' : 'carve-out',
  );
  const [metric, setMetric] = useState<MetricKey>('hc');
  const [focus, setFocus] = useState<string | null>(null);
  const [newName, setNewName] = useState('');

  const flows = useMemo(
    () => computeEntityFlows(state, perspective, metricFn(metric, state.assumptions)),
    [state, perspective, metric],
  );

  const format = (value: number): string =>
    metric === 'cost' ? fmtMoneyCompact(value) : fmt(Math.round(value * 100) / 100);

  const targetNames = useMemo(() => getEntityNames(state, perspective), [state, perspective]);
  const classOf = (name: string): EntityClassification =>
    entityClassOf(state, name) as EntityClassification;

  /*
    Everyone whose IMPORTED entity is this one.

    `remapEntity`'s schema is explicit that `employeeIds` is for the audit summary and its count, and
    that the mapping moves everyone whose as-is entity matches regardless. So the panel must pass the
    whole cohort: sending the twelve people visible in a filtered view would write a change-log entry
    saying "12 people" for a change that moved four hundred.
  */
  const cohortOf = (asIs: string): string[] =>
    state.employees
      .filter((p) => (String(p.legalEntity ?? '').trim() || '(No entity)') === asIs)
      .map((p) => p.id);

  const remap = (asIs: string, to: string): void => {
    const employeeIds = cohortOf(asIs);
    if (!employeeIds.length) return;
    onDispatch({
      type: 'remapEntity',
      scenarioId,
      perspective,
      fromEntity: asIs,
      toEntity: to,
      employeeIds,
    });
  };

  /** The as-is entities a mapping can act on — synthetic sources are not real entities. */
  const mappable = flows.sources.filter((s) => !/^\(/.test(s));

  /** What an as-is entity currently reads as, taken from the flows rather than re-derived. */
  const currentTargetOf = (asIs: string): string => {
    const out = flows.links.filter((l) => l.source === asIs);
    if (!out.length) return asIs;
    const biggest = out.reduce((a, b) => (b.value > a.value ? b : a));
    return biggest.target;
  };

  const addEntity = (name: string, classification: EntityClassification = 'new'): void => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onDispatch({
      type: 'addEntity',
      entityId: `en_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`,
      name: trimmed,
      country: null,
      classification,
    });
  };

  // Registry rows that were imported rather than invented: renamed through the mapping, not here.
  const asIsNames = new Set(flows.sources);

  if (!state.columnMap.legalEntity) {
    return (
      <Shell onClose={onClose}>
        <p className="muted" data-testid="entity-unmapped">
          Map a Legal Entity column first (Data → Review column mapping) to use entity transitions.
        </p>
      </Shell>
    );
  }

  return (
    <Shell onClose={onClose}>
      <div className="row" style={{ alignItems: 'center', gap: 12 }}>
        <span className="muted small">As-is →</span>
        <span className="lens" role="group" aria-label="Target side">
          {SIDES.map((s) => (
            <button
              key={s.value}
              data-testid={`entity-side-${s.value}`}
              className={perspective === s.value ? 'lens-on' : ''}
              aria-pressed={perspective === s.value}
              onClick={() => {
                setPerspective(s.value);
                setFocus(null);
              }}
            >
              {s.label}
            </button>
          ))}
        </span>
        <span className="spacer" />
        <button data-testid="entity-open-matrix" onClick={onOpenMatrix}>
          Open transition matrix →
        </button>
      </div>

      <MetricToggle
        value={metric}
        onChange={setMetric}
        keys={offeredMetrics(state, ['hc', 'fte', 'cost'])}
        weight="raw"
        testIdPrefix="entity-metric"
      />

      <EntitySankey
        flows={flows}
        classOf={classOf}
        format={format}
        focus={focus}
        onFocus={setFocus}
      />

      <h5 className="asm-grid-h">Move entities — everyone in an as-is entity transfers to its target</h5>
      <p className="muted asm-help">
        Individual exceptions are set in a person&rsquo;s side panel and override the entity mapping.
        Renaming a target onto an existing name merges them.
      </p>
      {mappable.length === 0 ? (
        <p className="muted" data-testid="entity-no-sources">
          No imported legal entities to map.
        </p>
      ) : (
        <table className="tsa-table" data-testid="entity-mapping-table">
          <thead>
            <tr>
              <th scope="col">As-is entity</th>
              <th scope="col" className="num">
                {metric === 'cost' ? 'Cost' : metric === 'fte' ? 'FTE' : 'Headcount'}
              </th>
              <th scope="col">{perspective === 'remain' ? 'RemainCo' : 'Carve-out'} entity</th>
              <th scope="col">Role</th>
            </tr>
          </thead>
          <tbody>
            {mappable.map((asIs) => {
              const target = currentTargetOf(asIs);
              return (
                <tr
                  key={asIs}
                  data-testid={`entity-map-row-${asIs}`}
                  className={focus && target === focus ? 'row-focus' : undefined}
                >
                  <td>{asIs}</td>
                  <td className="num">{format(flows.srcTotals[asIs] ?? 0)}</td>
                  <td>
                    <select
                      data-testid={`entity-target-select-${asIs}`}
                      value={targetNames.includes(target) ? target : ''}
                      disabled={readOnly}
                      aria-label={`Target entity for ${asIs}`}
                      onChange={(e) => {
                        if (e.target.value === '__new__') {
                          const name = window.prompt('Name the new target entity:', 'NewCo');
                          if (!name) return;
                          // The registry row first, then the mapping onto it — a mapping to a name
                          // nothing has declared would leave the target unclassified.
                          addEntity(name);
                          remap(asIs, name.trim());
                          return;
                        }
                        remap(asIs, e.target.value);
                      }}
                    >
                      {targetNames.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                      <option value="__new__">＋ New entity…</option>
                    </select>
                  </td>
                  <td>{ENTITY_CLASS_META[classOf(target)].label}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <h5 className="asm-grid-h">Target entity registry</h5>
      <p className="muted asm-help">
        What each entity is for. Removing one here only takes away its role — nobody moves, and an
        unregistered entity reads as Retain.
      </p>
      <table className="tsa-table" data-testid="entity-registry-table">
        <thead>
          <tr>
            <th scope="col">Entity</th>
            <th scope="col">Country</th>
            <th scope="col">Role</th>
            <th scope="col" />
          </tr>
        </thead>
        <tbody>
          {state.entities.map((entity) => {
            const imported = asIsNames.has(entity.name);
            return (
              <tr key={entity.id} data-testid={`entity-row-${entity.id}`}>
                <td>
                  {imported ? (
                    <span title="An imported entity — rename it through the mapping above">
                      {entity.name}
                    </span>
                  ) : (
                    <EntityCell
                      testId={`entity-name-${entity.id}`}
                      value={entity.name}
                      label={`Name of ${entity.name}`}
                      disabled={readOnly}
                      onCommit={(next) => {
                        const trimmed = next.trim();
                        // A blank name is not an edit — the field resyncs and nothing is sent.
                        if (!trimmed || trimmed === entity.name) return false;
                        onDispatch({
                          type: 'updateEntity',
                          entityId: entity.id,
                          patch: { name: trimmed },
                        });
                        return true;
                      }}
                    />
                  )}
                </td>
                <td>
                  <EntityCell
                    testId={`entity-country-${entity.id}`}
                    value={entity.country ?? ''}
                    label={`Country of ${entity.name}`}
                    disabled={readOnly}
                    onCommit={(next) => {
                      const trimmed = next.trim() || null;
                      if (trimmed === (entity.country ?? null)) return false;
                      onDispatch({
                        type: 'updateEntity',
                        entityId: entity.id,
                        patch: { country: trimmed },
                      });
                      return true;
                    }}
                  />
                </td>
                <td>
                  <select
                    data-testid={`entity-class-${entity.id}`}
                    value={entity.classification}
                    disabled={readOnly}
                    aria-label={`Role of ${entity.name}`}
                    onChange={(e) =>
                      onDispatch({
                        type: 'updateEntity',
                        entityId: entity.id,
                        patch: { classification: e.target.value as EntityClassification },
                      })
                    }
                  >
                    {CLASSES.map((c) => (
                      <option key={c} value={c}>
                        {ENTITY_CLASS_META[c].label}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  {/*
                    An imported entity has no delete: the registry row is what classifies it, and
                    removing it would silently return its people to Retain.
                  */}
                  {!imported && (
                    <IconButton
                      icon={ICON.remove}
                      testId={`entity-delete-${entity.id}`}
                      label={`Remove ${entity.name} from the registry`}
                      disabled={readOnly}
                      onClick={() => onDispatch({ type: 'deleteEntity', entityId: entity.id })}
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="field">
        <label htmlFor="entity-new-name">Add a target entity</label>
        <div className="row">
          <input
            id="entity-new-name"
            data-testid="entity-new-name"
            value={newName}
            disabled={readOnly}
            placeholder="NewCo GmbH"
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newName.trim()) {
                addEntity(newName);
                setNewName('');
              }
            }}
          />
          <button
            data-testid="entity-add"
            disabled={readOnly || !newName.trim()}
            onClick={() => {
              addEntity(newName);
              setNewName('');
            }}
          >
            ＋ Add
          </button>
        </div>
      </div>
    </Shell>
  );
}

/**
 * A registry cell that stays live without throwing away what somebody is typing.
 *
 * `useLiveInput` rather than `defaultValue` + `key={value}`: a key change is a REMOUNT, so a
 * colleague committing a change to the same cell would destroy the box mid-sentence. The registry is
 * project-wide and shared, which makes that collision likelier here than almost anywhere else.
 */
function EntityCell({
  testId,
  value,
  label,
  disabled,
  onCommit,
}: {
  testId: string;
  value: string;
  label: string;
  disabled: boolean;
  /** Returns whether an edit was actually sent, so a no-op leaves the field resynced. */
  onCommit(next: string): boolean;
}): JSX.Element {
  const live = useLiveInput(value);
  return (
    <input
      ref={live.ref}
      data-testid={testId}
      aria-label={label}
      disabled={disabled}
      onFocus={live.onFocus}
      onBlur={(e) => {
        if (!live.settle()) return;
        onCommit(e.target.value);
      }}
    />
  );
}

function Shell({ children, onClose }: { children: React.ReactNode; onClose(): void }): JSX.Element {
  return (
    <Workbench testId="entities-modal" title="Legal-entity transitions" onClose={onClose}>
      {children}
    </Workbench>
  );
}
